import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import Button from '../../common/Button';
import Modal from '../../common/Modal';
import PatientService from '../../../services/patientService';
import PatientSectionHeader from '../common/PatientSectionHeader';
import './PatientPhotos.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const PatientPhotos = () => {
  const { patientId } = useParams();
  const auth = useAuth();
  const [photos, setPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [patient, setPatient] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [noteValue, setNoteValue] = useState('');
  const [uploadWithNote, setUploadWithNote] = useState(false);
  const [uploadNote, setUploadNote] = useState('');

  const loadPhotos = async () => {
    try {
      setIsLoading(true);
      const [photosRes, patientData] = await Promise.all([
        fetch(`${API_BASE_URL}/patients/${patientId}/photos?sort=createdAt&order=desc`, {
          headers: {
            'Authorization': `Bearer ${auth.user?.access_token}`,
            'Content-Type': 'application/json'
          }
        }),
        PatientService.getPatientById(patientId, auth.user?.access_token)
      ]);
      
      if (!photosRes.ok) throw new Error('No se pudieron cargar las fotos');
      const photosData = await photosRes.json();
      
      // Ensure photos are sorted by createdAt descending (most recent first)
      const sortedPhotos = (photosData.data || []).sort((a, b) => {
        if (!a.createdAt && !b.createdAt) return 0;
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      
      setPhotos(sortedPhotos);
      setPatient(patientData);
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (patientId && auth.user) {
      loadPhotos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, auth.user?.access_token]);

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    try {
      await Promise.all(files.map(async (file) => {
        // 1) Get upload URL
        const resUrl = await fetch(`${API_BASE_URL}/patients/${patientId}/photos/upload-url`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${auth.user?.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ contentType: file.type, fileName: file.name })
        });
        if (!resUrl.ok) throw new Error(`No se pudo obtener URL de carga para ${file.name}`);
        const { data: { uploadUrl, photoId } } = await resUrl.json();

        // 2) Upload to S3
        const putRes = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file
        });
        if (!putRes.ok) throw new Error(`Fallo la carga del archivo ${file.name}`);

        // 3) Create record with description
        const resRecord = await fetch(`${API_BASE_URL}/patients/${patientId}/photos`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${auth.user?.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            photoId, 
            fileName: file.name, 
            contentType: file.type,
            description: uploadNote || null
          })
        });
        if (!resRecord.ok) throw new Error(`No se pudo crear el registro de la foto ${file.name}`);
      }));

      // Add a small delay to ensure server has processed the uploads
      await new Promise(resolve => setTimeout(resolve, 500));
      await loadPhotos();
      
      // Reset upload note
      setUploadNote('');
      setUploadWithNote(false);
    } catch (e) {
      alert(e.message);
    } finally {
      event.target.value = '';
    }
  };

  const handleDeleteClick = (photoId) => {
    setPendingDelete(photoId);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      const res = await fetch(`${API_BASE_URL}/patients/${patientId}/photos/${pendingDelete}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${auth.user?.access_token}` }
      });
      if (!res.ok) throw new Error('No se pudo eliminar la foto');
      await loadPhotos();
      setConfirmOpen(false);
      setPendingDelete(null);
    } catch (e) {
      alert(e.message);
    }
  };

  const handleEditNote = (photo) => {
    setEditingNote(photo.photoId);
    setNoteValue(photo.description || '');
  };

  const handleSaveNote = async (photoId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/patients/${patientId}/photos/${photoId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${auth.user?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ description: noteValue })
      });
      if (!res.ok) throw new Error('No se pudo actualizar la nota');
      
      // Update local state
      setPhotos(prevPhotos => 
        prevPhotos.map(photo => 
          photo.photoId === photoId 
            ? { ...photo, description: noteValue }
            : photo
        )
      );
      
      setEditingNote(null);
      setNoteValue('');
    } catch (e) {
      alert(e.message);
    }
  };

  const handleCancelEdit = () => {
    setEditingNote(null);
    setNoteValue('');
  };

  const handleUploadClick = () => {
    if (uploadWithNote) {
      setUploadWithNote(false);
      setUploadNote('');
    } else {
      document.getElementById('photo-upload-input').click();
    }
  };

  // Group photos by date (photos are already sorted by createdAt desc)
  const groupPhotosByDate = (photos) => {
    const groups = {};
    
    // Photos are already sorted, so we just need to group them
    photos.forEach(photo => {
      if (photo.createdAt) {
        const date = new Date(photo.createdAt).toLocaleDateString('es-ES', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
        if (!groups[date]) {
          groups[date] = [];
        }
        groups[date].push(photo);
      } else {
        // Handle photos without createdAt
        const unknownDate = 'Fecha desconocida';
        if (!groups[unknownDate]) {
          groups[unknownDate] = [];
        }
        groups[unknownDate].push(photo);
      }
    });
    
    // Sort groups by date (most recent first)
    const sortedGroups = Object.entries(groups).sort(([dateA], [dateB]) => {
      if (dateA === 'Fecha desconocida') return 1;
      if (dateB === 'Fecha desconocida') return -1;
      
      // Parse dates for comparison
      const parseDateA = new Date(dateA.split(' de ').reverse().join('-'));
      const parseDateB = new Date(dateB.split(' de ').reverse().join('-'));
      
      return parseDateB - parseDateA;
    });
    
    return sortedGroups;
  };

  const photoGroups = groupPhotosByDate(photos);

  if (isLoading) return <div className="patient-photos-loading"><div className="loading-spinner"></div></div>;
  if (error) return <div className="patient-photos-error">{error}</div>;

  return (
    <div className="patient-photos-container">
      <PatientSectionHeader
        patientId={patientId}
        patientName={patient ? `${patient.firstName} ${patient.lastName}` : null}
        sectionTitle="Fotos del Paciente"
        actionButton={
          <div className="upload-actions">
            <Button 
              variant="outline" 
              icon="fas fa-plus"
              onClick={() => setUploadWithNote(true)}
            >
              Subir con nota
            </Button>
            <Button 
              variant="primary" 
              icon="fas fa-upload"
              onClick={handleUploadClick}
            >
              Subir foto
            </Button>
          </div>
        }
      />
      
      {/* Upload with note form */}
      {uploadWithNote && (
        <div className="upload-with-note-form">
          <div className="form-group">
            <label htmlFor="upload-note">Nota para las fotos:</label>
            <textarea
              id="upload-note"
              value={uploadNote}
              onChange={(e) => setUploadNote(e.target.value)}
              placeholder="Agregar una nota que se aplicará a todas las fotos subidas..."
              rows={3}
            />
          </div>
          <div className="upload-note-actions">
            <Button
              variant="outline"
              onClick={() => {
                setUploadWithNote(false);
                setUploadNote('');
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              icon="fas fa-upload"
              onClick={() => document.getElementById('photo-upload-input').click()}
            >
              Seleccionar fotos
            </Button>
          </div>
        </div>
      )}
      
      {/* Hidden file input */}
      <input 
        id="photo-upload-input"
        type="file" 
        accept="image/*" 
        multiple 
        onChange={handleFileChange} 
        style={{ display: 'none' }} 
      />
      
      {photos.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-camera"></i>
          <p>No hay fotos aún</p>
        </div>
      ) : (
        <div className="photos-by-date">
          {photoGroups.map(([date, datePhotos]) => (
            <div key={date} className="photos-date-group">
              <div className="photos-date-header">
                <h3 className="photos-date-title">
                  <i className="fas fa-calendar-day"></i>
                  {date}
                  <span className="photos-count">({datePhotos.length} foto{datePhotos.length !== 1 ? 's' : ''})</span>
                </h3>
              </div>
              <div className="photos-grid">
                {datePhotos.map(photo => (
                  <div className="photo-card" key={photo.photoId}>
                    <img src={photo.url} alt={photo.fileName} />
                    
                    {/* Photo description/note section */}
                    <div className="photo-description">
                      {editingNote === photo.photoId ? (
                        <div className="edit-note-form">
                          <textarea
                            value={noteValue}
                            onChange={(e) => setNoteValue(e.target.value)}
                            placeholder="Agregar nota..."
                            rows={2}
                          />
                          <div className="edit-note-actions">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleCancelEdit}
                              icon="fas fa-times"
                            >
                            </Button>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleSaveNote(photo.photoId)}
                              icon="fas fa-save"
                            >
                              Guardar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="photo-note-display">
                          {photo.description ? (
                            <p className="photo-note">{photo.description}</p>
                          ) : (
                            <p className="no-note">Sin nota</p>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="photo-actions">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(photo.url, '_blank', 'noopener,noreferrer')}
                        icon="fas fa-eye"
                      >
                        Ver
                      </Button>
                      <div className="photo-actions-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditNote(photo)}
                          icon="fas fa-edit"
                          title={photo.description ? 'Editar nota' : 'Agregar nota'}
                        />
                        <Button 
                          variant="danger" 
                          size="sm" 
                          onClick={() => handleDeleteClick(photo.photoId)} 
                          icon="fas fa-trash"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={confirmOpen}
        title="Confirmar eliminación"
        onClose={() => setConfirmOpen(false)}
        footer={(
          <>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} icon="fas fa-times">Cancelar</Button>
            <Button variant="danger" onClick={confirmDelete} icon="fas fa-trash">Eliminar</Button>
          </>
        )}
      >
        <p>¿Seguro que deseas eliminar esta foto? Esta acción no se puede deshacer.</p>
      </Modal>
    </div>
  );
};

export default PatientPhotos;
